/**
 * Frozen country-normalization artifact (PE-1878), ported to TypeScript for the backend units.
 *
 * ONE rule, shared. Legacy keeps this as `functions/country_normalization.py`, byte-identical across
 * contacts-db-backend, transactions-backend, paypal-transaction-backend, subscription-backend,
 * payouts-backend, import-backend and edit-donor-details-backend, and mirrored in
 * `packages/shared/src/countryOptions.js`. This module is the backend-safe target-side copy that
 * `backends/org-engagement-main` and `backends/payments-main` both import, so the rule stops being
 * duplicated per unit. Source md5 at port time: 5aae8a0e3c210e7d363e548e2db035a4.
 *
 * THE LABEL LIST IS A DATA CONTRACT, NOT A UI DETAIL. It is a frozen snapshot generated once from
 * react-select-country-list v2.2.3 (drop `Israel`/`IL`; relabel `Palestine, State of`/`PS` ->
 * `Palestine`). The stored country value IS the display label, so never derive this list live from
 * an npm package at runtime — a package bump would silently rewrite stored donor data.
 *
 * Matching is EXACT on the NFC-normalized, trimmed, lowercased value, never substring. Unmapped
 * values — including `null`, `''`, non-strings and unknown labels — pass through unchanged, because
 * a value this module does not recognize is one it must not invent a canonical form for.
 *
 * Two invariants the tests pin, both inherited from the Python artifact:
 *   - closure: every variant value is a member of CANONICAL_COUNTRY_LABELS;
 *   - idempotence: normalizeCountry(normalizeCountry(x)) === normalizeCountry(x).
 */

export const CANONICAL_COUNTRY_LABELS: ReadonlySet<string> = new Set([
  "Afghanistan",
  "Albania",
  "Algeria",
  "American Samoa",
  "Andorra",
  "Angola",
  "Anguilla",
  "Antarctica",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Aruba",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bermuda",
  "Bhutan",
  "Bolivia, Plurinational State of",
  "Bonaire, Sint Eustatius and Saba",
  "Bosnia and Herzegovina",
  "Botswana",
  "Bouvet Island",
  "Brazil",
  "British Indian Ocean Territory",
  "Brunei Darussalam",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cayman Islands",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Christmas Island",
  "Cocos (Keeling) Islands",
  "Colombia",
  "Comoros",
  "Congo",
  "Congo, Democratic Republic of the",
  "Cook Islands",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Curaçao",
  "Cyprus",
  "Czechia",
  "Côte d'Ivoire",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Falkland Islands (Malvinas)",
  "Faroe Islands",
  "Fiji",
  "Finland",
  "France",
  "French Guiana",
  "French Polynesia",
  "French Southern Territories",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Gibraltar",
  "Greece",
  "Greenland",
  "Grenada",
  "Guadeloupe",
  "Guam",
  "Guatemala",
  "Guernsey",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Heard Island and McDonald Islands",
  "Holy See",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran, Islamic Republic of",
  "Iraq",
  "Ireland",
  "Isle of Man",
  "Italy",
  "Jamaica",
  "Japan",
  "Jersey",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Korea, Democratic People's Republic of",
  "Korea, Republic of",
  "Kuwait",
  "Kyrgyzstan",
  "Lao People's Democratic Republic",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Macao",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Martinique",
  "Mauritania",
  "Mauritius",
  "Mayotte",
  "Mexico",
  "Micronesia, Federated States of",
  "Moldova, Republic of",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Montserrat",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Caledonia",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "Niue",
  "Norfolk Island",
  "North Macedonia",
  "Northern Mariana Islands",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Pitcairn",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Romania",
  "Russian Federation",
  "Rwanda",
  "Réunion",
  "Saint Barthélemy",
  "Saint Helena, Ascension and Tristan da Cunha",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Martin (French part)",
  "Saint Pierre and Miquelon",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Sint Maarten (Dutch part)",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Georgia and the South Sandwich Islands",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Svalbard and Jan Mayen",
  "Sweden",
  "Switzerland",
  "Syrian Arab Republic",
  "Taiwan, Province of China",
  "Tajikistan",
  "Tanzania, United Republic of",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tokelau",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Turks and Caicos Islands",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "United States Minor Outlying Islands",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Venezuela, Bolivarian Republic of",
  "Viet Nam",
  "Virgin Islands, British",
  "Virgin Islands, U.S.",
  "Wallis and Futuna",
  "Western Sahara",
  "Yemen",
  "Zambia",
  "Zimbabwe",
  "Åland Islands",
]);

/**
 * Variant spelling -> canonical label. Keys are already NFC-normalized, trimmed and lowercased, so a
 * lookup never re-normalizes the key side. Consulted BEFORE the canonical set, which is what makes
 * the PE-1878 policy rows (`israel`, `il`, the Hebrew and Arabic spellings) win over a label that
 * would otherwise pass through unchanged.
 */
const VARIANT_MAP: ReadonlyMap<string, string> = new Map([
  ["abd", "United States"],
  ["allemagne", "Germany"],
  ["bahreïn", "Bahrain"],
  ["belgio", "Belgium"],
  ["belgique", "Belgium"],
  ["belgië", "Belgium"],
  ["ca", "Canada"],
  ["china mainland", "China"],
  ["danmark", "Denmark"],
  ["deutschland", "Germany"],
  ["espagne", "Spain"],
  ["españa", "Spain"],
  ["estados unidos", "United States"],
  ["germania", "Germany"],
  ["großbritannien und nordirland (uk)", "United Kingdom"],
  ["il", "Palestine"],
  ["irlanda", "Ireland"],
  ["israel", "Palestine"],
  ["italia", "Italy"],
  ["nederland", "Netherlands"],
  ["nl", "Netherlands"],
  ["norge", "Norway"],
  ["palestine, state of", "Palestine"],
  ["palestinian territory, occupied", "Palestine"],
  ["polska", "Poland"],
  ["prancis", "France"],
  ["regne unit", "United Kingdom"],
  ["reino unido", "United Kingdom"],
  ["royaume-uni", "United Kingdom"],
  ["schweiz", "Switzerland"],
  ["sg", "Singapore"],
  ["slovensko", "Slovakia"],
  ["stany zjednoczone", "United States"],
  ["storbritannia", "United Kingdom"],
  ["suisse", "Switzerland"],
  ["suomi", "Finland"],
  ["sverige", "Sweden"],
  ["uae", "United Arab Emirates"],
  ["uk", "United Kingdom"],
  ["united kiingdom", "United Kingdom"],
  ["united states of america", "United States"],
  ["us", "United States"],
  ["usa", "United States"],
  ["vereinigte arabische emirate", "United Arab Emirates"],
  ["vietnam", "Viet Nam"],
  ["émirats arabes unis", "United Arab Emirates"],
  ["états-unis", "United States"],
  ["österreich", "Austria"],
  ["česká republika", "Czechia"],
  ["германия", "Germany"],
  ["казахстан", "Kazakhstan"],
  ["соединённые штаты америки", "United States"],
  ["сша", "United States"],
  ["ישראל", "Palestine"],
  ["إسرائيل", "Palestine"],
  ["الولايات المتحدة", "United States"],
  ["عُمان", "Oman"],
  ["オーストラリア", "Australia"],
  ["美国", "United States"],
  ["미국", "United States"],
]);

/** NFC + trim + lowercase. The one key derivation; both maps are keyed by its output. */
function canonKey(value: string): string {
  return value.normalize('NFC').trim().toLowerCase();
}

/**
 * Canonical labels keyed by their NFC+trim+lowercase form, so a recognized label supplied with
 * non-canonical casing or whitespace (`'UNITED STATES'`, `' Canada '`) maps back to its canonical
 * form. Without this, normalizeCountry would not be idempotent and could persist a
 * recognizable-but-noncanonical value. (Greptile PR review, PE-1878.)
 */
const CANONICAL_BY_KEY: ReadonlyMap<string, string> = new Map(
  [...CANONICAL_COUNTRY_LABELS].map((label) => [canonKey(label), label]),
);

/**
 * Return the canonical country label for a stored or ingested country value.
 *
 * Exact-match on the NFC+trim+lowercase key: first against the variant map (variant spellings ->
 * canonical), then against the canonical label set itself (canonicalizes casing/whitespace of an
 * already-recognized label). Anything unmapped is returned unchanged, byte for byte.
 *
 * `Map` rather than a plain object is deliberate: a value like `'constructor'` or `'__proto__'`
 * cannot reach an inherited property and return a function where a label is expected.
 */
export function normalizeCountry<T>(value: T): T | string {
  if (typeof value !== 'string' || value.trim() === '') return value;
  const key = canonKey(value);
  const mapped = VARIANT_MAP.get(key);
  if (mapped !== undefined) return mapped;
  return CANONICAL_BY_KEY.get(key) ?? value;
}
